import React, { useCallback } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useAuthenticatedUser } from 'src/hooks/useAuthenticatedUser';
import { getPartnerCompanyEvent, getPartnerCompanyEventApprovals } from 'src/api/partner';
import TasksTab from 'src/components/partner-portal/TasksTab';
import { Spinner } from '@repo/components';
import { Alert } from '@repo/components';
import { constructQuery } from '@repo/shared/helpers';
import { useBreadcrumbLevel } from '@repo/components/hooks';

export const Route = createFileRoute('/partner_/$partnerId/events/$eventId/approvals/')({
	component: RouteComponent,
	validateSearch: (search: Record<string, unknown>) => ({
		page: search.page !== undefined ? Number(search.page) : 0,
		sort: (search.sort as string) || 'updatedAt:desc',
	}),
});

function RouteComponent() {
	const { user: authenticatedUser, loading: userLoading } = useAuthenticatedUser();
	const { partnerId, eventId } = Route.useParams();
	useBreadcrumbLevel(3, 'Approvals');
	const { page, sort } = Route.useSearch();
	const queryClient = useQueryClient();
	const navigate = useNavigate();

	// Get event data
	const {
		data: event,
		isLoading: eventLoading,
		error: eventError,
	} = useQuery({
		queryKey: ['partner-company-event', partnerId, eventId],
		queryFn: () => getPartnerCompanyEvent(partnerId, eventId),
		enabled: !userLoading && !!authenticatedUser,
	});

	// Get event approvals
	const { data: approvals, isLoading: approvalsLoading } = useQuery({
		queryKey: ['partner-company-event-approvals', partnerId, eventId, { page, sort }],
		queryFn: () => getPartnerCompanyEventApprovals(partnerId, eventId, constructQuery({ page: page.toString(), sort })),
		enabled: !userLoading && !!authenticatedUser && !!event,
	});

	const handleReload = useCallback(() => {
		queryClient.invalidateQueries({ queryKey: ['partner-company-event-approvals', partnerId, eventId] });
	}, [queryClient, partnerId, eventId]);

	const handleUpdate = useCallback(() => {
		queryClient.invalidateQueries({ queryKey: ['partner-company-event', partnerId, eventId] });
		queryClient.invalidateQueries({ queryKey: ['partner-company-event-approvals', partnerId, eventId] });
	}, [queryClient, partnerId, eventId]);

	const handleTaskSelected = useCallback(
		(taskId: string) => {
			navigate({ to: `/partner/${partnerId}/events/${eventId}/approvals/${taskId}` });
		},
		[navigate, partnerId, eventId],
	);

	if (userLoading || eventLoading || approvalsLoading) {
		return (
			<div className="flex items-center justify-center h-full">
				<Spinner className="text-[#1E1E1E]" />
			</div>
		);
	}

	if (eventError || !event) {
		return (
			<div className="p-6">
				<Alert variant="destructive">Event not found</Alert>
			</div>
		);
	}

	return (
		<div className="p-6">
			<TasksTab
				tasks={approvals}
				authenticatedUser={authenticatedUser}
				onUpdate={handleUpdate}
				onReload={handleReload}
				onTaskSelected={handleTaskSelected}
				filter="all"
				sort={sort}
			/>
		</div>
	);
}
